import Service from '@ember/service';
import setDefaultOptions from 'linced-app/utils/set-default-options';
import { saveAs } from 'file-saver';

const defaultConfig = {
  sheetName: 'Sheet1',
  download: true,
  returnBlob: false,
  fileName: 'export.xlsx',
  multiSheet: false,
  tableParseOptions: {},
};

/**
 * Generates and downloads Excel (`.xlsx`) workbooks from in-memory data or
 * HTML tables.
 *
 * Extracted from `@linc-technologies/ember-spreadsheet-export`.
 * For plain-text CSV output, use the companion `csv` service instead.
 *
 * Sheet data can be either:
 *  - an array of arrays, where each inner array is one row and the first
 *    row is the heading row, or
 *  - an `HTMLTableElement`, which is parsed via SheetJS `table_to_sheet`
 *    (colspan/rowspan become merged cells automatically).
 *
 * ```javascript
 * import Component from '@glimmer/component';
 * import { service } from '@ember/service';
 * import { action } from '@ember/object';
 *
 * export default class ExportButton extends Component {
 *   @service excel;
 *
 *   // Single sheet from an array of rows
 *   @action exportRows() {
 *     this.excel.export(
 *       [
 *         ['Title 1', 'Title 2', 'Title 3'],
 *         ['row1cell1', 'row1cell2', 'row1cell3'],
 *         ['row2cell1', 'row2cell2', 'row2cell3'],
 *       ],
 *       { sheetName: 'Sheet1', fileName: 'export.xlsx' }
 *     );
 *   }
 *
 *   // Single sheet from a rendered HTML table
 *   @action exportTable() {
 *     this.excel.export(document.querySelector('#dataTable'), {
 *       sheetName: 'Table 1',
 *       fileName: 'export-table.xlsx',
 *     });
 *   }
 *
 *   // Multi-sheet workbook; sheets can mix arrays and HTML tables
 *   @action exportWorkbook() {
 *     const sheets = [
 *       {
 *         name: 'Demo sheet',
 *         data: [
 *           ['Foo', 'Bar'],
 *           ['Baz', 'Foobar'],
 *         ],
 *       },
 *       {
 *         name: 'HTML table',
 *         data: document.querySelector('#dataTable'),
 *       },
 *     ];
 *     this.excel.export(sheets, {
 *       multiSheet: true,
 *       fileName: 'export-multisheet.xlsx',
 *     });
 *   }
 * }
 * ```
 *
 * ## Merging cells
 *
 * Pass an array of `merges` (in `options` for a single sheet, or per sheet
 * object when `multiSheet`). Each merge is `{ s, e }` marking the start and
 * end of the merged range, where `r` is the zero-based row index and `c`
 * the zero-based column index. This example merges the first two cells of
 * the first column:
 *
 * ```javascript
 * this.excel.export(data, {
 *   fileName: 'export.xlsx',
 *   merges: [{ s: { r: 0, c: 0 }, e: { r: 1, c: 0 } }],
 * });
 * ```
 */
export default class ExcelService extends Service {
  /**
   * Builds an `.xlsx` workbook from `data` and (by default) triggers a
   * browser download.
   *
   * Cell types are inferred per value: numbers and booleans keep their
   * type, moment objects (anything with a `_d` Date property) become
   * date-formatted numeric cells, and everything else is written as a
   * string. Null/undefined cells are left empty.
   *
   * @param {Array<Array>|HTMLTableElement|Array<{name: string, data: (Array<Array>|HTMLTableElement), merges: Array=}>} data
   *   A single sheet's rows or HTML table, or, when `options.multiSheet` is
   *   true, an array of `{ name, data, merges }` sheet objects.
   * @param {Object} [options]
   * @param {string} [options.sheetName='Sheet1'] - Worksheet name (single-sheet
   *   mode only; multi-sheet names come from each sheet object).
   * @param {boolean} [options.download=true] - Trigger a file download via file-saver.
   * @param {boolean} [options.returnBlob=false] - Return the generated `Blob`
   *   instead of (or as well as) downloading. Useful for uploading or testing.
   * @param {string} [options.fileName='export.xlsx'] - Downloaded file name.
   * @param {boolean} [options.multiSheet=false] - Treat `data` as an array of
   *   sheet objects and produce one worksheet per entry.
   * @param {Array<{s: {r: number, c: number}, e: {r: number, c: number}}>} [options.merges]
   *   Cell ranges to merge (single-sheet mode only; see class docs).
   * @param {Object} [options.tableParseOptions={}] - Passed through to SheetJS
   *   `XLSX.utils.table_to_sheet` when `data` is an HTML table (e.g. `{ raw: true }`).
   * @returns {Promise<Blob|undefined>} The xlsx blob when `options.returnBlob` is true.
   */
  async export(data, options) {
    options = setDefaultOptions(options, defaultConfig);
    // SheetJS is large. Load it only when an export starts.
    const { default: XLSX } = await import('xlsx');

    function s2ab(s) {
      let buf = new ArrayBuffer(s.length);
      let view = new Uint8Array(buf);
      for (let i = 0; i !== s.length; ++i) {
        view[i] = s.charCodeAt(i) & 0xff;
      }
      return buf;
    }

    function datenum(v, date1904) {
      if (date1904) {
        v += 1462;
      }
      let epoch = Date.parse(v);
      return (epoch - new Date(Date.UTC(1899, 11, 30))) / (24 * 60 * 60 * 1000);
    }

    function sheet_from_data(data, options) {
      if (data instanceof HTMLTableElement) {
        return XLSX.utils.table_to_sheet(data, options.tableParseOptions);
      }
      return sheet_from_array_of_arrays(data);
    }

    function sheet_from_array_of_arrays(data) {
      let ws = {};
      let range = { s: { c: 10000000, r: 10000000 }, e: { c: 0, r: 0 } };
      for (let R = 0; R !== data.length; ++R) {
        for (let C = 0; C !== data[R].length; ++C) {
          if (range.s.r > R) {
            range.s.r = R;
          }
          if (range.s.c > C) {
            range.s.c = C;
          }
          if (range.e.r < R) {
            range.e.r = R;
          }
          if (range.e.c < C) {
            range.e.c = C;
          }
          let cell = { v: data[R][C] };
          if (cell.v == null) {
            continue;
          }
          let cell_ref = XLSX.utils.encode_cell({ c: C, r: R });

          if (typeof cell.v === 'number') {
            cell.t = 'n';
          } else if (typeof cell.v === 'boolean') {
            cell.t = 'b';
          } else if (typeof cell.v === 'object' && cell.v._d instanceof Date) {
            cell.t = 'n';
            cell.z = XLSX.SSF._table[14];
            cell.v = datenum(cell.v._d);
          } else {
            cell.t = 's';
          }

          ws[cell_ref] = cell;
        }
      }
      if (range.s.c < 10000000) {
        ws['!ref'] = XLSX.utils.encode_range(range);
      }
      return ws;
    }

    function Workbook() {
      if (!(this instanceof Workbook)) {
        return new Workbook();
      }
      this.SheetNames = [];
      this.Sheets = {};
    }

    let wb = new Workbook();

    if (options.multiSheet) {
      // Add multiple worksheets to workbook
      data.forEach((sheet) => {
        wb.SheetNames.push(sheet.name);
        wb.Sheets[sheet.name] = sheet_from_data(sheet.data, options);
        if (sheet.merges && sheet.merges.length) {
          wb.Sheets[sheet.name]['!merges'] = sheet.merges;
        }
      });
    } else {
      // Add a single worksheet to workbook
      wb.SheetNames.push(options.sheetName);
      wb.Sheets[options.sheetName] = sheet_from_data(data, options);
      if (options.merges && options.merges.length) {
        wb.Sheets[options.sheetName]['!merges'] = options.merges;
      }
    }

    let wbout = XLSX.write(wb, {
      bookType: 'xlsx',
      bookSST: true,
      type: 'binary',
    });

    let output = new Blob([s2ab(wbout)], { type: 'application/octet-stream' });

    if (options.download) {
      saveAs(output, options.fileName);
    }
    if (options.returnBlob) {
      return output;
    }
  }
}
